import { MenuIcons } from '@/shared/config/menu-icons';
import { APP_ROUTES } from '@/shared/config/routes-config';
import { MenuItems } from '@/shared/enums/side-menu.enum';
import { UserRole } from '@/shared/enums/user-role.enum';
import { UserTeams } from '@/shared/enums/user-teams.enum';
import { MenuConfigItem } from '@/shared/interfaces/side-menu-item.interface';
import { sideMenuMapper } from '@/shared/mappers/side-menu.mapper';
import { isUserSalesTeam } from '@/shared/services/helpers/auth-helper/auth-helper.service';

// Helper function to get localized display name
export const getLocalizedDisplayName = (
  displayName: string,
  t: (key: string) => string
): string => {
  const displayNameMap: { [key: string]: string } = {
    DASHBOARD: t('SIDE_MENU.DASHBOARD'),
    'FINANCE NOW!': t('SIDE_MENU.FINANCE_NOW'),
    COMMUNICATION: t('SIDE_MENU.COMMUNICATION'),
    Whatsapp: t('SIDE_MENU.WHATSAPP'),
    'Corporate Whatsapp': t('SIDE_MENU.CORPORATE_WHATSAPP'),
    OPERATIONS: t('SIDE_MENU.OPERATIONS'),
    Transactions: t('SIDE_MENU.TRANSACTIONS'),
    'Purchase orders': t('SIDE_MENU.PURCHASE_ORDERS'),
    LEADS: t('SIDE_MENU.LEADS'),
    Dashboard: t('SIDE_MENU.DASHBOARD'),
    Leads: t('SIDE_MENU.LEADS'),
    'Applications Dashboard': t('SIDE_MENU.APPLICATIONS_DASHBOARD'),
    Profiles: t('SIDE_MENU.PROFILES'),
    'Finance Applications': t('SIDE_MENU.FINANCE_APPLICATIONS'),
    'Service Center Requests': t('SIDE_MENU.SERVICE_CENTER_REQUESTS'),
    REQUESTS: t('SIDE_MENU.REQUESTS'),
    CUSTOMERS: t('SIDE_MENU.CUSTOMERS'),
    PARTNERS: t('SIDE_MENU.PARTNERS'),
    Merchants: t('SIDE_MENU.MERCHANTS'),
    Referrals: t('SIDE_MENU.REFERRALS'),
    PRODUCTS: t('SIDE_MENU.PRODUCTS'),
    'Auto products': t('SIDE_MENU.AUTO_PRODUCTS'),
    'Financial products': t('SIDE_MENU.FINANCIAL_PRODUCTS'),
    'Service center products': t('SIDE_MENU.SERVICE_CENTER_PRODUCTS'),
    REPORTS: t('SIDE_MENU.REPORTS'),
    USERS: t('SIDE_MENU.USERS'),
    Teams: t('SIDE_MENU.TEAMS'),
    'Commission scheme': t('SIDE_MENU.COMMISSION_SCHEME'),
    Archive: t('SIDE_MENU.ARCHIVE'),
    'Auto brands': t('SIDE_MENU.AUTO_BRANDS'),
    SALES: t('SIDE_MENU.SALES'),
    SETTINGS: t('SIDE_MENU.SETTINGS'),
    'Purchase Orders': t('SIDE_MENU.PURCHASE_ORDERS'),
    Accounts: t('SIDE_MENU.ACCOUNTS'),
    Wallets: t('SIDE_MENU.WALLETS'),
    'Cash outs': t('SIDE_MENU.CASHOUTS'),
    'Cash ins': t('SIDE_MENU.CASHINS'),
    ATTENDANCE: t('SIDE_MENU.ATTENDANCE'),
    HELPDESK: t('SIDE_MENU.HELPDESK'),
    'Helpdesk Dashboard': t('SIDE_MENU.HELPDESK_DASHBOARD'),
    'All Tickets': t('SIDE_MENU.HELPDESK_ALL_TICKETS'),
    'My Tickets': t('SIDE_MENU.HELPDESK_MY_TICKETS')
  };

  return displayNameMap[displayName] || displayName;
};

/*  Logic  
    - We extract from APP_ROUTES every route with IS_MENU = TRUE 
    - Create 'menuList' empty array
    - We check if the route has PARENT
      >> if false, we add the route in the 'menuList'
      >> if true, we check if there is a parent in the 'menuList' exists with the same name
        >> if false, we create a parent and add the route as a child for it
        >> if true, we add the route as a child for that parent
*/

export const getMenuList = (
  userRole?: UserRole[],
  userTeam?: UserTeam[],
  t?: (key: string) => string
): MenuConfigItem[] => {
  const routeMenuList: MenuConfigItem[] = [];
  Object.keys(APP_ROUTES).forEach((routeItemKey: string) => {
    const isPermittedRole = APP_ROUTES[routeItemKey].PERMITTED_ROLES
      ? userRole &&
        (APP_ROUTES[routeItemKey].PERMITTED_ROLES as UserRole[]).filter((x) =>
          userRole?.includes(x)
        )?.length > 0
      : true;

    const isPermittedTeam = !isUserSalesTeam()
      ? isPermittedRole
      : APP_ROUTES[routeItemKey].PERMITTED_TEAMS
      ? userTeam &&
        userTeam?.length > 0 &&
        (APP_ROUTES[routeItemKey].PERMITTED_TEAMS as UserTeams[]).filter((x) =>
          userTeam?.some((t) => t.name === x)
        )?.length > 0
      : true;

    if (APP_ROUTES[routeItemKey].IS_MENU && isPermittedRole && isPermittedTeam) {
      const menuItem = sideMenuMapper(routeItemKey);
      // Apply localization if translation function is provided
      if (t && menuItem.DISPLAY_NAME) {
        menuItem.DISPLAY_NAME = getLocalizedDisplayName(menuItem.DISPLAY_NAME, t);
      }
      routeMenuList.push(menuItem);
    }
  });

  const menuList: MenuConfigItem[] = [];
  routeMenuList.map((item) => {
    if (item.PARENT) {
      // Check if parent already exists by comparing the original PARENT value, not localized DISPLAY_NAME
      const existingParentIndex = menuList.findIndex(
        (menuItem) => menuItem.NAME === item.PARENT?.toLowerCase()
      );

      if (existingParentIndex !== -1) {
        // Parent exists, add this item as a child
        menuList[existingParentIndex].CHILDREN?.push(item);
      } else {
        // Parent doesn't exist, create new parent
        const parentDisplayName =
          t && item.PARENT ? getLocalizedDisplayName(item.PARENT, t) : item.PARENT;
        menuList.push({
          FULL_PATH: '',
          NAME: item.PARENT?.toLowerCase(),
          DISPLAY_NAME: parentDisplayName,
          ICON: MenuIcons[MenuItems[item.PARENT as MenuItems]],
          CHILDREN: [item]
        });
      }
    } else {
      menuList.push(item);
    }
  });

  return menuList;
};
